import {
  DishAllergen,
  DishIngredient,
  DishPreference,
  DishResponse,
  DishTag,
} from "api-types";

import React, { useCallback, useMemo, useState } from "react";

import { Box, Stack, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

import {
  CheckCircleRounded,
  NotInterestedRounded,
  SyncAlt,
} from "@mui/icons-material";

import { useActionTracking } from "../../../analytics/actions-context";
import { DatadogActionNames } from "../../../analytics/datadog-action-names";
import { useDeviceDimensions } from "../../../hooks/useDeviceDimensions";
import {
  calculatePreferenceDisclaimers,
  groupIngredientsByDisclaimer,
} from "../../../utils/disclaimer-grouping";
import {
  AllergenChipOutlined,
  ChilliIcon,
  PreferenceChipOutlined,
  TagChip,
} from "../../Chip/Chip";
import { ImagePreview } from "../../ImagePreview/ImagePreview";
import { AllergenIngredientsDrawer } from "../AllergenIngredientsDrawer/AllergenIngredientsDrawer";
import { DishDisclaimerDrawer } from "../DishDisclaimerDrawer/DishDisclaimerDrawer";
import { DishInfoDrawer } from "../DishInfoDrawer/DishInfoDrawer";
import ArrowRightIcon from "../assets/arrow-right.svg";
import MayContainIcon from "../assets/may-contain.svg";
import { useFilterMenu } from "../filters";
import { DishMayContain } from "./DishMayContain";

interface DishProps {
  dish: DishResponse;
  restaurantName: string;
  isIngredientFilteringEnabled?: boolean;
  priceFormatter: (priceInLowestDenomination: number) => string;

  containerRef?: React.RefObject<HTMLDivElement>;
}

/**
 * A component for a dish in the menu
 */
export const Dish = ({
  dish,
  restaurantName,
  isIngredientFilteringEnabled = false,
  priceFormatter,
  containerRef,
}: DishProps) => {
  const {
    palette: { oscar },
  } = useTheme();
  const filterMenu = useFilterMenu();
  const { isMobile } = useDeviceDimensions();

  const [openAllergenIngredientsDrawer, setOpenAllergenIngredientsDrawer] =
    useState(false);
  const [openDishDisclaimerDrawer, setOpenDishDisclaimerDrawer] =
    useState(false);
  const price = useMemo(() => {
    return priceFormatter(dish.priceInLowestDenomination);
  }, [dish.priceInLowestDenomination, priceFormatter]);

  const allergenDisclaimers = useMemo(() => {
    const allergenDisclaimers = filterMenu.disclaimers.filter(({ type }) =>
      ["undisclosed-ingredient", "cross-contamination"].includes(type)
    );
    return groupIngredientsByDisclaimer({
      restaurantName,
      disclaimers: allergenDisclaimers,
      dishIngredients: dish.ingredients,
    }).filter(({ ingredients }) => ingredients.length > 0);
  }, [restaurantName, dish, filterMenu]);

  const preferenceDisclaimers = useMemo(() => {
    const prefDisclaimers = filterMenu.disclaimers.filter(({ type }) =>
      ["various-sources"].includes(type)
    );
    return calculatePreferenceDisclaimers({
      restaurantName,
      selectedPreference: filterMenu.selectedPreference,
      disclaimers: prefDisclaimers,
      dishIngredients: dish.ingredients,
    }).filter(({ ingredients }) => ingredients.length > 0);
  }, [restaurantName, dish, filterMenu]);

  const matchedIngredients = useMemo(() => {
    return filterMenu.selectedIngredients.flatMap((ingredient) =>
      dish.ingredients.filter(
        (dishIngredient) =>
          dishIngredient.igfdTaxIngredientId === ingredient.igfdTaxIngredientId
      )
    );
  }, [dish.ingredients, filterMenu.selectedIngredients]);

  const matchedAllergens = useMemo(() => {
    return filterMenu.selectedAllergens.flatMap((allergen) =>
      dish.allergens.filter(
        (dishAllergen) =>
          dishAllergen.igfdTaxAllergenId === allergen.igfdTaxAllergenId
      )
    );
  }, [dish.allergens, filterMenu.selectedAllergens]);

  const matchedRecommendTags = useMemo(() => {
    return filterMenu.selectedTags.flatMap((tag) =>
      dish.tags.filter(
        (dishTag) =>
          dishTag.displayIntent === "recommend" && dishTag.id === tag.id
      )
    );
  }, [dish.tags, filterMenu.selectedTags]);

  const matchedNeutralTags = useMemo(() => {
    return filterMenu.selectedTags.flatMap((tag) =>
      dish.tags.filter(
        (dishTag) =>
          dishTag.displayIntent === "neutral" && dishTag.id === tag.id
      )
    );
  }, [dish.tags, filterMenu.selectedTags]);

  // matched avoid tags?

  const matchedPreferences = useMemo(() => {
    return dish.preferences.filter(
      (pref) =>
        pref.igfdTaxPreferenceId ===
        (filterMenu.selectedPreference?.igfdTaxPreferenceId as string)
    );
  }, [dish.preferences, filterMenu.selectedPreference]);
  const hasAllergens = !!matchedAllergens.length;
  const hasFilteredAllergensOrIngredient =
    filterMenu.selectedAllergens.length > 0 ||
    filterMenu.selectedIngredients.length > 0;

  const matchedMayContainAllergens = useMemo(() => {
    if (!filterMenu.showMayContainAllergens) {
      // This feature is disabled
      return [];
    }
    return filterMenu.selectedAllergens.flatMap((allergen) =>
      dish.mayContainAllergens.filter(
        (dishMayContainAllergen) =>
          dishMayContainAllergen.igfdTaxAllergenId ===
          allergen.igfdTaxAllergenId
      )
    );
  }, [
    dish.mayContainAllergens,
    filterMenu.selectedAllergens,
    filterMenu.showMayContainAllergens,
  ]);

  const [dishInfoDrawerOpen, setDishInfoDrawerOpen] = useState(false);

  const showMayContains =
    (hasFilteredAllergensOrIngredient && !!allergenDisclaimers.length) ||
    (filterMenu.showMayContainAllergens && !!matchedMayContainAllergens.length);

  const compatibilityType = useMemo(() => {
    return matchedAllergens.length > 0 || matchedIngredients.length > 0
      ? "allergy"
      : showMayContains
      ? "may-contain"
      : matchedPreferences.length > 0
      ? "preference"
      : "standard";
  }, [
    matchedPreferences,
    matchedAllergens,
    matchedIngredients,
    matchedMayContainAllergens,
  ]);

  const specialIcon =
    compatibilityType === "allergy" ? (
      <NotInterestedRounded
        fontSize="small"
        sx={{ color: oscar.light.content.danger }}
      />
    ) : compatibilityType === "may-contain" ? (
      <img src={MayContainIcon} alt="" style={{ width: 20 }} />
    ) : compatibilityType === "preference" ? (
      <CheckCircleRounded
        fontSize={"small"}
        sx={{ color: oscar.light.icon.accent }}
      />
    ) : null;

  const hasMatchingAllergens =
    matchedAllergens.length > 0 || matchedIngredients.length > 0;

  const hasMatchingAllergensAndPreferences =
    matchedPreferences.length > 0 || hasMatchingAllergens;

  const highlightColor =
    compatibilityType === "allergy"
      ? oscar.light.icon.danger
      : compatibilityType === "preference"
      ? oscar.light.icon.accent
      : undefined;

  const { trackDishSelected } = useActionTracking();
  const onDishClick = useCallback(() => {
    setDishInfoDrawerOpen(true);
    trackDishSelected(dish.id);
  }, [dish.id, trackDishSelected]);

  return (
    <>
      <Stack
        sx={() => ({
          cursor: "pointer",
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          padding: "24px 0",
        })}
        gap={2}
        onClick={onDishClick}
        data-dd-action-name={DatadogActionNames.dishSelected(dish.name)}
      >
        <Stack
          direction="column"
          gap={1.5}
          flex={1}
          marginLeft={isMobile ? 1 : 0}
          marginRight={isMobile ? 1 : 0}
        >
          <Stack
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <HeaderRow>
              {specialIcon}
              <DishTitle name={dish.name} titleColor={highlightColor} />
            </HeaderRow>
            <Stack alignItems="center" flexDirection="row" gap={2}>
              {!!dish.spiceLevel || price ? (
                <SpiceAndPrice price={price} spiceLevel={dish.spiceLevel} />
              ) : null}
              <img src={ArrowRightIcon} alt="" style={{ marginTop: -3 }} />
            </Stack>
          </Stack>
          {dish.description ? (
            <Typography
              color={oscar.light.content.content1}
              variant="bodyRLight"
            >
              {dish.description}
            </Typography>
          ) : null}
          {(dish.caloriesInKcal && filterMenu.showCalories) ||
          !!matchedNeutralTags.length ? (
            <Box alignItems="center" display="flex" gap={2}>
              {dish.caloriesInKcal && filterMenu.showCalories && (
                <Typography
                  color={oscar.light.content.content2}
                  variant="bodyRLight"
                >
                  {dish.caloriesInKcal} kcal
                </Typography>
              )}
              {matchedNeutralTags?.map((tag) => (
                <TagChip key={tag.id} tag={tag} />
              ))}
            </Box>
          ) : null}
          {hasMatchingAllergensAndPreferences ||
          matchedRecommendTags.length > 0 ? (
            <MatchedAllergensAndPreferences
              hasMayNotBePreference={!!preferenceDisclaimers.length}
              hasMatchingAllergens={hasMatchingAllergens}
              matchedPreferences={matchedPreferences}
              matchedAllergens={matchedAllergens}
              matchedIngredients={matchedIngredients}
              matchedRecommendTags={matchedRecommendTags}
            />
          ) : null}

          {showMayContains && (
            <DishMayContain
              allergenDisclaimers={allergenDisclaimers}
              hasFilteredAllergensOrIngredient={
                hasFilteredAllergensOrIngredient
              }
              hasMatchingAllergens={hasMatchingAllergens}
              matchedMayContainAllergens={matchedMayContainAllergens}
            />
          )}
        </Stack>
        {dish.imageUrl ? (
          <ImagePreview imageUrl={dish.imageUrl} height="90px" width="160px" />
        ) : null}
      </Stack>
      <DishInfoDrawer
        dish={dish}
        formattedPrice={price}
        specialIcon={specialIcon}
        highlightColor={highlightColor}
        matchedRecommendTags={matchedRecommendTags}
        matchedNeutralTags={matchedNeutralTags}
        matchedAllergens={matchedAllergens}
        matchedMayContainAllergens={matchedMayContainAllergens}
        allergenDisclaimers={allergenDisclaimers}
        preferenceDisclaimers={preferenceDisclaimers}
        isIngredientFilteringEnabled={isIngredientFilteringEnabled}
        matchedPreferences={matchedPreferences}
        matchedIngredients={matchedIngredients}
        showMayContains={showMayContains}
        hasFilteredAllergensOrIngredient={hasFilteredAllergensOrIngredient}
        open={dishInfoDrawerOpen}
        onOpenAllergenIngredients={() => setOpenAllergenIngredientsDrawer(true)}
        onClose={() => setDishInfoDrawerOpen(false)}
        showCalories={filterMenu.showCalories}
        containerRef={containerRef}
      />
      <AllergenIngredientsDrawer
        dish={dish}
        containerRef={containerRef}
        matchedAllergens={matchedAllergens}
        open={openAllergenIngredientsDrawer}
        onClose={() => setOpenAllergenIngredientsDrawer(false)}
      />
      <DishDisclaimerDrawer
        open={openDishDisclaimerDrawer}
        onClose={() => setOpenDishDisclaimerDrawer(false)}
      />
    </>
  );
};

/**
 * Provides the header for the dish.
 */
const DishTitle = ({
  name,
  titleColor,
}: {
  name: string;
  titleColor?: string;
}) => {
  return (
    <Stack
      direction={"row"}
      gap={2}
      sx={{
        display: "flex",
        alignItems: "center",
        paddingRight: "20px",
      }}
    >
      <Typography
        noWrap
        variant="labelLMedium"
        sx={{
          color: titleColor,
          maxWidth: "100%",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          whiteSpace: "normal",
        }}
      >
        {name}
      </Typography>
    </Stack>
  );
};

const ALLERGENS_TO_SHOW = 5;

const MatchedAllergensAndPreferences = ({
  hasMatchingAllergens,
  matchedAllergens,
  matchedPreferences,
  matchedIngredients,
  matchedRecommendTags,
  hasMayNotBePreference,
}: {
  hasMatchingAllergens: boolean;
  matchedAllergens?: DishAllergen[];
  matchedPreferences: DishPreference[];
  matchedIngredients: DishIngredient[];
  matchedRecommendTags: DishTag[];
  hasMayNotBePreference?: boolean;
}) => {
  const filterMenu = useFilterMenu();
  const hasOptionalAllergens = useMemo(
    () => matchedAllergens?.some((allergen) => allergen.isOptional),
    [matchedAllergens]
  );
  const showMoreAllergens =
    matchedAllergens && matchedAllergens?.length > ALLERGENS_TO_SHOW;
  const initialAllergens = useMemo(
    () =>
      matchedAllergens && [...matchedAllergens]?.splice(0, ALLERGENS_TO_SHOW),
    [matchedAllergens]
  );

  return (
    <Stack
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        width: "100%",
      }}
    >
      {hasMatchingAllergens && filterMenu.showAllergenDetails && (
        <>
          {initialAllergens?.map((allergen) => (
            <Box
              key={allergen.igfdTaxAllergenId}
              alignItems="center"
              display="flex"
              whiteSpace="nowrap"
            >
              <AllergenChipOutlined
                allergenName={allergen.allergenName}
                canOmit={allergen.isOptional}
              />
            </Box>
          ))}
          {showMoreAllergens && matchedAllergens.length && (
            <AllergenChipOutlined
              allergenName={`+${matchedAllergens.length - ALLERGENS_TO_SHOW}`}
            />
          )}
          {hasOptionalAllergens ? (
            <Box
              alignItems="center"
              display="flex"
              marginLeft={1}
              marginRight={2}
            >
              <SyncAlt
                sx={({ palette }) => ({
                  fontSize: 12,
                  color: palette.secondary.main,
                  marginRight: 1,
                })}
              />
              <Typography variant="body2">Allergens can be removed</Typography>
            </Box>
          ) : null}
          {matchedIngredients?.map((ingredient) => (
            <Box
              key={ingredient.igfdTaxIngredientId}
              alignItems="center"
              display="flex"
              whiteSpace="nowrap"
            >
              <AllergenChipOutlined allergenName={ingredient.ingredientName} />
            </Box>
          ))}
        </>
      )}
      {!filterMenu.showAllergenDetails && hasMatchingAllergens && (
        <AllergenChipOutlined allergenName="Not suitable." link="Why?" />
      )}

      {(!hasMayNotBePreference || filterMenu.showAllergenDetails) &&
        matchedPreferences?.map((preference) => (
          <PreferenceChipOutlined
            key={preference.igfdTaxPreferenceId}
            preferenceName={preference.preferenceName}
          />
        ))}
      {!filterMenu.showAllergenDetails && hasMayNotBePreference && (
        <PreferenceChipOutlined
          preferenceName="May not be vegetarian."
          mayNotBe
          link="Why?"
        />
      )}
      {matchedRecommendTags?.map((tag) => (
        <TagChip key={tag.id} tag={tag} />
      ))}
    </Stack>
  );
};

const SpiceAndPrice = ({
  spiceLevel,
  price,
}: {
  spiceLevel?: number;
  price: string;
}) => {
  const { isLargeScreen } = useDeviceDimensions();
  return (
    <Stack
      direction={isLargeScreen ? "column" : "column-reverse"}
      gap={isLargeScreen ? 0.5 : 0}
      alignItems={"end"}
      width={55}
    >
      {!!price ? (
        <Typography
          variant={"body1"}
          align="right"
          sx={(theme) => ({ fontFamily: theme.typography.fontFamily })}
        >
          {price}
        </Typography>
      ) : null}
      {!!spiceLevel && (
        <Typography
          variant={"body1"}
          align="right"
          sx={(theme) => ({
            fontFamily: theme.typography.fontFamily,
            minWidth: 70,
          })}
        >
          {ChilliIcon.repeat(spiceLevel)}
        </Typography>
      )}
    </Stack>
  );
};

const HeaderRow = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 8,
}));
